// The HTTP implementation of `ApiClient`.
//
// Everything the app reads or writes passes through here: one base URL, one
// bearer token, one error shape. The client never composes a filter
// expression — every query parameter is named and typed (api-contract §0.5),
// which was both the coupling and the injection surface in the old data layer.

import { currentConfig, loadConfig } from './config'
import { apiToken, graphToken, initAuth, signIn } from './auth'
import { ApiError, type ApiErrorBody } from './errors'
import type { ApiClient } from './contract'
import type {
  Account,
  AnalyticsAging,
  AnalyticsCorrections,
  AnalyticsDenials,
  AnalyticsOverview,
  AnalyticsParams,
  AnalyticsSpendByEntity,
  AnalyticsThroughput,
  Assignment,
  AssignmentListParams,
  AssignmentRequest,
  AssignmentResult,
  AssociatedEntity,
  InvoiceLineItem,
  InvoiceLineItemWrite,
  Company,
  CompanyEmailDomain,
  CompanyScanner,
  IntakeSender,
  Conversation,
  Correction,
  CorrectionListParams,
  Decision,
  DecisionBatchRequest,
  DecisionBatchResult,
  DecisionListParams,
  DecisionRequest,
  DecisionResult,
  DirectoryUser,
  ExportBatch,
  ExportDetail,
  ExportResult,
  Flag,
  FlagDefinition,
  Hold,
  IntakeSaveRequest,
  IntakeSaveResult,
  Invoice,
  InvoiceBundle,
  InvoiceInclude,
  InvoiceListParams,
  Page,
  PresignRequest,
  PresignResult,
  Profile,
  QueueCounts,
  QueueStuck,
  ReferenceListParams,
  RegisterUploadRequest,
  ReminderSubscription,
  ReminderWrite,
  SignedUrl,
  Upload,
  UserRole,
  UserRoleToken,
  Vehicle,
  VehicleImportPreview,
  VehicleImportResult,
  Vendor,
  VendorAlias,
  VendorCodingRule,
  CostCenter,
  ExpenseType,
} from './types'

// ---------------------------------------------------------------------------
// Money: decimal string on the wire, number in the app (see types.ts).
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>

const INVOICE_MONEY = ['subtotal', 'tax', 'total'] as const
// Decimal-string fields on a line item. `quantity` is in here for the READ side (it
// arrives as a decimal string and the app works in numbers) but it is NOT money:
// the column is `numeric(14,4)`, so writing it through the two-decimal formatter
// rounded 0.3333 hours to 0.33 and silently changed the line.
const LINE_ITEM_MONEY = ['quantity', 'unit_price', 'total'] as const
const LINE_ITEM_MONEY_WRITE = ['unit_price', 'total'] as const
const LINE_ITEM_QUANTITY = ['quantity'] as const

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/** Two-decimal decimal string; the API rejects a JSON number for money. */
function toMoneyString(value: unknown): string | null {
  const n = toNumber(value)
  return n === null ? null : n.toFixed(2)
}

function parseMoneyFields<T>(row: unknown, fields: readonly string[]): T {
  if (!row || typeof row !== 'object') return row as T
  const out = { ...(row as Row) }
  for (const f of fields) if (f in out) out[f] = toNumber(out[f])
  return out as T
}

function parseInvoice(row: unknown): Invoice {
  return parseMoneyFields<Invoice>(row, INVOICE_MONEY)
}

function parseLineItem(row: unknown): InvoiceLineItem {
  return parseMoneyFields<InvoiceLineItem>(row, LINE_ITEM_MONEY)
}

/** Writes go the other way: numbers become decimal strings. */
function writeMoneyFields(body: Row, fields: readonly string[]): Row {
  const out = { ...body }
  for (const f of fields) {
    if (f in out && out[f] !== null && out[f] !== undefined) out[f] = toMoneyString(out[f])
    // an explicit null stays null — that is a field clear (§0.7)
  }
  return out
}

/** `numeric(14,4)` as a decimal string, e.g. `"2.5000"` — four places, because
    quantity is not money and two would round a fractional hour or gallon away. */
function toQuantityString(value: unknown): string | null {
  const n = toNumber(value)
  return n === null ? null : n.toFixed(4)
}

/** A line item write: money at two decimals, `quantity` at four. Exported so the
    wire format is pinned by a test rather than by reading this file. */
export function writeLineItemFields(body: Row): Row {
  const out = writeMoneyFields(body, LINE_ITEM_MONEY_WRITE)
  for (const f of LINE_ITEM_QUANTITY) {
    if (f in out && out[f] !== null && out[f] !== undefined) out[f] = toQuantityString(out[f])
  }
  return out
}

/** Money inside an aggregate row: the shapes differ per endpoint, so the field
    is named rather than inferred. */
function parseTotals<T>(rows: T[] | undefined, field = 'total'): T[] {
  return (rows ?? []).map((r) => ({ ...(r as object), [field]: toNumber((r as Row)[field]) }) as T)
}

// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------

type QueryValue = string | number | boolean | undefined | null | (string | number)[]

function query(params: Record<string, QueryValue> = {}): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      for (const v of value) sp.append(key, String(v))
    } else {
      sp.set(key, String(value))
    }
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

interface RequestOptions {
  method?: string
  body?: unknown
  /** Multipart body; skips the JSON content type. */
  form?: FormData
  idempotencyKey?: string
  /** Absolute URL (Graph); skips the API base and the API token. */
  absolute?: string
  token?: () => Promise<string>
  expectEmpty?: boolean
}

class HttpApiClient implements ApiClient {
  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = options.absolute ?? `${currentConfig().api_base_url.replace(/\/$/, '')}${path}`
    const token = await (options.token ?? apiToken)()
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    }
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
    let body: BodyInit | undefined
    if (options.form) {
      body = options.form
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(options.body)
    }

    const res = await fetch(url, { method: options.method ?? 'GET', headers, body })

    if (!res.ok) {
      let parsed: ApiErrorBody = { code: `http_${res.status}`, message: res.statusText }
      try {
        const json = (await res.json()) as { error?: ApiErrorBody }
        if (json?.error) parsed = json.error
      } catch {
        // 401 from the gateway has an empty body, by design.
        if (res.status === 401) parsed = { code: 'unauthenticated', message: 'Your session has expired. Sign in again.' }
      }
      throw new ApiError(res.status, parsed)
    }

    if (options.expectEmpty || res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  private newIdempotencyKey(): string {
    return crypto.randomUUID()
  }

  // --- session ------------------------------------------------------------

  getProfile(): Promise<Profile> {
    return this.request<Profile>('/me')
  }

  // --- queues -------------------------------------------------------------

  getQueueCounts(params: { workspace?: string } = {}): Promise<QueueCounts> {
    return this.request<QueueCounts>(`/queues/counts${query({ workspace: params.workspace ?? 'invoice' })}`)
  }

  getStuckQueues(params: { workspace?: string } = {}): Promise<QueueStuck> {
    return this.request<QueueStuck>(`/queues/stuck${query({ workspace: params.workspace ?? 'invoice' })}`)
  }

  // --- invoices -----------------------------------------------------------

  async listInvoices(params: InvoiceListParams = {}): Promise<Page<Invoice>> {
    const page = await this.request<Page<Invoice>>(
      `/invoices${query({
        status: params.status,
        company_id: params.company_id,
        vendor_id: params.vendor_id,
        date_from: params.date_from,
        date_to: params.date_to,
        total_min: params.total_min === undefined ? undefined : toMoneyString(params.total_min),
        total_max: params.total_max === undefined ? undefined : toMoneyString(params.total_max),
        export_id: params.export_id,
        upload_id: params.upload_id,
        manual_entry: params.manual_entry,
        unresolved_flags: params.unresolved_flags,
        flag_type: params.flag_type,
        q: params.q,
        since: params.since,
        sort: params.sort,
        view: params.view,
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    )
    return { ...page, data: (page.data ?? []).map(parseInvoice) }
  }

  async getInvoice(invoiceId: string, include: InvoiceInclude[] = []): Promise<InvoiceBundle> {
    const bundle = await this.request<InvoiceBundle>(
      `/invoices/${encodeURIComponent(invoiceId)}${query({ include: include.length ? include.join(',') : undefined })}`,
    )
    return {
      ...bundle,
      invoice: parseInvoice(bundle.invoice),
      line_items: bundle.line_items?.map(parseLineItem),
    }
  }

  async intakeSave(invoiceId: string, body: IntakeSaveRequest): Promise<IntakeSaveResult> {
    const payload = {
      ...body,
      invoice: writeMoneyFields(body.invoice as Row, INVOICE_MONEY),
      line_items: body.line_items
        ? {
            upsert: body.line_items.upsert?.map((c) => writeLineItemFields(c as Row)),
            remove: body.line_items.remove,
          }
        : undefined,
    }
    const result = await this.request<IntakeSaveResult>(
      `/invoices/${encodeURIComponent(invoiceId)}/intake-save`,
      { method: 'POST', body: payload },
    )
    return {
      ...result,
      invoice: parseInvoice(result.invoice),
      line_items: result.line_items?.map(parseLineItem),
    }
  }

  // --- line items ------------------------------------------------------------

  async listLineItems(invoiceId: string): Promise<InvoiceLineItem[]> {
    const res = await this.request<{ data: InvoiceLineItem[] }>(`/invoices/${encodeURIComponent(invoiceId)}/line-items`)
    return (res.data ?? []).map(parseLineItem)
  }

  async listLineItemsForInvoices(invoiceIds: string[]): Promise<InvoiceLineItem[]> {
    if (invoiceIds.length === 0) return []
    const out: InvoiceLineItem[] = []
    // The API caps a multi-id read at 200 ids (§0.4).
    for (let i = 0; i < invoiceIds.length; i += 200) {
      const slice = invoiceIds.slice(i, i + 200)
      const res = await this.request<{ data: InvoiceLineItem[] }>(`/line-items${query({ invoice_ids: slice.join(',') })}`)
      out.push(...(res.data ?? []).map(parseLineItem))
    }
    return out
  }

  async createLineItem(invoiceId: string, body: InvoiceLineItemWrite): Promise<InvoiceLineItem> {
    const res = await this.request<{ line_item: InvoiceLineItem }>(`/invoices/${encodeURIComponent(invoiceId)}/line-items`, {
      method: 'POST',
      body: writeLineItemFields(body as Row),
    })
    return parseLineItem(res.line_item)
  }

  async updateLineItem(invoiceId: string, lineItemId: string, body: InvoiceLineItemWrite): Promise<InvoiceLineItem> {
    const res = await this.request<{ line_item: InvoiceLineItem }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}`,
      { method: 'PATCH', body: writeLineItemFields(body as Row) },
    )
    return parseLineItem(res.line_item)
  }

  deleteLineItem(invoiceId: string, lineItemId: string, rowVersion: number): Promise<void> {
    return this.request<void>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}${query({ row_version: rowVersion })}`,
      { method: 'DELETE', expectEmpty: true },
    )
  }

  async setLineItemAccount(
    invoiceId: string,
    lineItemId: string,
    body: { account_id: string; row_version?: number },
  ): Promise<{ line_item: InvoiceLineItem; correction_id?: string }> {
    const res = await this.request<{ line_item: InvoiceLineItem; correction_id?: string }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}/account`,
      { method: 'PATCH', body },
    )
    return { ...res, line_item: parseLineItem(res.line_item) }
  }
  async setLineItemExpenseType(
    invoiceId: string,
    lineItemId: string,
    body: { expense_type_code: string | null; row_version?: number },
  ): Promise<{ line_item: InvoiceLineItem; correction_id?: string }> {
    const res = await this.request<{ line_item: InvoiceLineItem; correction_id?: string }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}/expense-type`,
      { method: 'PATCH', body },
    )
    return { ...res, line_item: parseLineItem(res.line_item) }
  }
  async setLineItemCostCenter(
    invoiceId: string,
    lineItemId: string,
    body: { cost_center_code: string | null; row_version?: number },
  ): Promise<{ line_item: InvoiceLineItem; correction_id?: string }> {
    const res = await this.request<{ line_item: InvoiceLineItem; correction_id?: string }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}/cost-center`,
      { method: 'PATCH', body },
    )
    return { ...res, line_item: parseLineItem(res.line_item) }
  }
  async setLineItemCostCentersBulk(
    invoiceId: string,
    body: {
      cost_center_code: string
      line_items: { invoice_line_item_id: string; row_version: number }[]
    },
  ): Promise<{ line_items: InvoiceLineItem[]; correction_ids: string[] }> {
    // POST, not PATCH: the router has no literal-over-parameter precedence and
    // `PATCH …/line-items/{id}` has the same segment count, so a PATCH here
    // would address a line item named `cost-center`.
    const res = await this.request<{ line_items: unknown[]; correction_ids?: string[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/cost-center`,
      { method: 'POST', body },
    )
    return {
      line_items: (res.line_items ?? []).map(parseLineItem),
      correction_ids: res.correction_ids ?? [],
    }
  }

  // --- flags --------------------------------------------------------------

  async listFlags(invoiceId: string, params: { unresolved?: boolean } = {}): Promise<Flag[]> {
    const res = await this.request<{ data: Flag[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/flags${query({ unresolved: params.unresolved })}`,
    )
    return res.data ?? []
  }

  resolveFlag(invoiceId: string, flagId: string): Promise<{ flag: Flag; unresolved_flag_count: number }> {
    return this.request(`/invoices/${encodeURIComponent(invoiceId)}/flags/${encodeURIComponent(flagId)}/resolve`, {
      method: 'POST',
      body: {},
    })
  }

  async listFlagDefinitions(): Promise<FlagDefinition[]> {
    const res = await this.request<{ data: FlagDefinition[] }>('/flag-definitions')
    return res.data ?? []
  }

  // --- decisions ----------------------------------------------------------

  async listDecisions(invoiceId: string): Promise<Decision[]> {
    const res = await this.request<{ data: Decision[] }>(`/invoices/${encodeURIComponent(invoiceId)}/decisions`)
    return res.data ?? []
  }

  async createDecision(body: DecisionRequest): Promise<DecisionResult> {
    const result = await this.request<DecisionResult>('/decisions', {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
    return { ...result, invoice: parseInvoice(result.invoice) }
  }

  createDecisionBatch(body: DecisionBatchRequest): Promise<DecisionBatchResult> {
    return this.request('/decisions/batch', {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
  }

  listDenialDecisions(params: DecisionListParams): Promise<Page<Decision>> {
    return this.request(
      `/decisions${query({
        since: params.since,
        decision: params.decision,
        step: params.step,
        sort: 'decided_at_desc',
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    )
  }

  // --- holds --------------------------------------------------------------

  async listHolds(invoiceId: string, params: { active?: boolean } = {}): Promise<Hold[]> {
    const res = await this.request<{ data: Hold[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/holds${query({ active: params.active })}`,
    )
    return res.data ?? []
  }

  async createHold(invoiceId: string, body: { reason: string }): Promise<Hold> {
    const res = await this.request<{ hold: Hold }>(`/invoices/${encodeURIComponent(invoiceId)}/holds`, {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
    return res.hold
  }

  async releaseHold(holdId: string): Promise<Hold> {
    const res = await this.request<{ hold: Hold }>(`/holds/${encodeURIComponent(holdId)}/release`, { method: 'POST', body: {} })
    return res.hold
  }

  // --- conversations ------------------------------------------------------

  async listConversations(invoiceId: string): Promise<Conversation[]> {
    const res = await this.request<{ data: Conversation[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/conversations`,
    )
    return res.data ?? []
  }

  createConversation(
    invoiceId: string,
    body: { body: string; mentions: string[] },
  ): Promise<{ conversation: Conversation; notifications_queued: number }> {
    return this.request(`/invoices/${encodeURIComponent(invoiceId)}/conversations`, { method: 'POST', body })
  }

  // --- associated entities ------------------------------------------------

  async listAssociatedEntities(invoiceId: string): Promise<AssociatedEntity[]> {
    const res = await this.request<{ data: AssociatedEntity[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/associated-entities`,
    )
    return res.data ?? []
  }

  async createAssociatedEntity(invoiceId: string, body: Partial<AssociatedEntity>): Promise<AssociatedEntity> {
    const res = await this.request<{ associated_entity: AssociatedEntity }>(
      `/invoices/${encodeURIComponent(invoiceId)}/associated-entities`,
      { method: 'POST', body },
    )
    return res.associated_entity
  }

  async updateAssociatedEntity(entityId: string, body: Partial<AssociatedEntity>): Promise<AssociatedEntity> {
    const res = await this.request<{ associated_entity: AssociatedEntity }>(
      `/associated-entities/${encodeURIComponent(entityId)}`,
      { method: 'PATCH', body },
    )
    return res.associated_entity
  }

  deleteAssociatedEntity(entityId: string, rowVersion: number): Promise<void> {
    return this.request<void>(
      `/associated-entities/${encodeURIComponent(entityId)}${query({ row_version: rowVersion })}`,
      { method: 'DELETE', expectEmpty: true },
    )
  }

  // --- uploads ------------------------------------------------------------

  presignUpload(body: PresignRequest): Promise<PresignResult> {
    return this.request('/uploads/presign', { method: 'POST', body })
  }

  async putUploadFile(presigned: PresignResult, file: File): Promise<void> {
    const form = new FormData()
    for (const [k, v] of Object.entries(presigned.fields ?? {})) form.append(k, v)
    form.append('file', file)
    // A presigned S3 POST is not an API call: no bearer token, no base URL.
    const res = await fetch(presigned.url, { method: 'POST', body: form })
    if (!res.ok) {
      throw new ApiError(res.status, {
        code: 'upload_failed',
        message: `The file could not be stored (HTTP ${res.status}).`,
      })
    }
  }

  async registerUpload(body: RegisterUploadRequest, idempotencyKey: string): Promise<Upload> {
    const res = await this.request<{ upload: Upload }>('/uploads/register', {
      method: 'POST',
      body,
      idempotencyKey,
    })
    return res.upload
  }

  async getUpload(uploadId: string): Promise<Upload> {
    const res = await this.request<{ upload: Upload }>(`/uploads/${encodeURIComponent(uploadId)}`)
    return res.upload
  }

  getUploadPdfUrl(uploadId: string): Promise<SignedUrl> {
    return this.request(`/uploads/${encodeURIComponent(uploadId)}/pdf-url`)
  }

  // --- exports ------------------------------------------------------------

  async listExports(params: { limit?: number } = {}): Promise<ExportBatch[]> {
    const res = await this.request<{ data: ExportBatch[] }>(
      `/exports${query({ sort: 'exported_at_desc', limit: params.limit ?? 10 })}`,
    )
    return res.data ?? []
  }

  createExport(invoiceIds: string[], idempotencyKey?: string): Promise<ExportResult> {
    return this.request('/exports', {
      method: 'POST',
      body: { invoice_ids: invoiceIds },
      idempotencyKey: idempotencyKey ?? this.newIdempotencyKey(),
    })
  }

  getExport(exportId: string): Promise<ExportDetail> {
    return this.request(`/exports/${encodeURIComponent(exportId)}`)
  }

  getExportFileUrl(exportId: string, fileId: string): Promise<SignedUrl> {
    return this.request(
      `/exports/${encodeURIComponent(exportId)}/files/${encodeURIComponent(fileId)}/download-url`,
    )
  }

  // --- assignments --------------------------------------------------------

  listAssignments(params: AssignmentListParams = {}): Promise<Page<Assignment>> {
    return this.request(
      `/assignments${query({
        workspace: params.workspace,
        role: params.role,
        company_id: params.company_id,
        active: params.active,
        user_id: params.user_id,
        sort: 'created_at_desc',
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    )
  }

  async getAssignment(assignmentId: string): Promise<Assignment> {
    const res = await this.request<{ assignment: Assignment }>(`/assignments/${encodeURIComponent(assignmentId)}`)
    return res.assignment
  }

  createAssignment(body: AssignmentRequest): Promise<AssignmentResult> {
    return this.request('/assignments', {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
  }

  // --- internal step roles ------------------------------------------------

  async listUserRoles(
    params: { workspace?: string; role?: UserRoleToken; user_id?: string } = {},
  ): Promise<UserRole[]> {
    const res = await this.request<{ data: UserRole[] }>(`/user-roles${query({ ...params })}`)
    return res.data ?? []
  }

  async createUserRole(body: {
    workspace: string
    role: UserRoleToken
    user_id: string
    user_upn: string
    user_name?: string | null
    user_email?: string | null
  }): Promise<UserRole> {
    const res = await this.request<{ user_role: UserRole }>('/user-roles', { method: 'POST', body })
    return res.user_role
  }

  deleteUserRole(userRoleId: string): Promise<void> {
    return this.request<void>(`/user-roles/${encodeURIComponent(userRoleId)}`, {
      method: 'DELETE',
      expectEmpty: true,
    })
  }

  // --- reminder subscriptions --------------------------------------------

  async listReminderSubscriptions(): Promise<ReminderSubscription[]> {
    const res = await this.request<{ data: ReminderSubscription[] }>('/reminder-subscriptions')
    return res.data ?? []
  }

  async createReminderSubscription(body: ReminderWrite): Promise<ReminderSubscription> {
    const res = await this.request<{ subscription: ReminderSubscription }>('/reminder-subscriptions', {
      method: 'POST',
      body,
    })
    return res.subscription
  }

  replaceReminderSubscription(
    id: string,
    body: ReminderWrite & { row_version: number },
  ): Promise<{ subscription: ReminderSubscription; superseded_id: string }> {
    return this.request(`/reminder-subscriptions/${encodeURIComponent(id)}`, { method: 'PUT', body })
  }

  async setReminderSubscriptionStatus(
    id: string,
    status: 'active' | 'paused' | 'cancelled',
  ): Promise<ReminderSubscription> {
    const res = await this.request<{ subscription: ReminderSubscription }>(
      `/reminder-subscriptions/${encodeURIComponent(id)}/status`,
      { method: 'PATCH', body: { status } },
    )
    return res.subscription
  }

  // --- reference data -----------------------------------------------------

  listCompanies(params: ReferenceListParams = {}): Promise<Page<Company>> {
    return this.request(`/companies${query({ ...params })}`)
  }

  async createCompany(body: { company_id: string; company_name: string }): Promise<Company> {
    const res = await this.request<{ company: Company }>('/companies', { method: 'POST', body })
    return res.company
  }

  async updateCompany(
    companyId: string,
    body: { company_name?: string; is_active?: boolean; row_version: number },
  ): Promise<Company> {
    const res = await this.request<{ company: Company }>(`/companies/${encodeURIComponent(companyId)}`, {
      method: 'PATCH',
      body,
    })
    return res.company
  }

  listCompanyEmailDomains(
    params: { company_id?: string; active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<CompanyEmailDomain>> {
    return this.request(`/company-email-domains${query({ ...params })}`)
  }

  async createCompanyEmailDomain(body: { domain: string; company_id: string }): Promise<CompanyEmailDomain> {
    const res = await this.request<{ company_email_domain: CompanyEmailDomain }>('/company-email-domains', {
      method: 'POST',
      body,
    })
    return res.company_email_domain
  }

  async updateCompanyEmailDomain(
    id: string,
    body: { company_id?: string; is_active?: boolean; row_version: number },
  ): Promise<CompanyEmailDomain> {
    const res = await this.request<{ company_email_domain: CompanyEmailDomain }>(
      `/company-email-domains/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.company_email_domain
  }

  async listCompanyScanners(
    params: { company_id?: string; active?: boolean } = {},
  ): Promise<CompanyScanner[]> {
    const res = await this.request<{ data: CompanyScanner[] }>(`/company-scanners${query({ ...params })}`)
    return res.data ?? []
  }

  async createCompanyScanner(body: { scanner_name: string; company_id: string }): Promise<CompanyScanner> {
    const res = await this.request<{ company_scanner: CompanyScanner }>('/company-scanners', {
      method: 'POST',
      body,
    })
    return res.company_scanner
  }

  async updateCompanyScanner(
    id: string,
    body: { company_id?: string; is_active?: boolean; row_version: number },
  ): Promise<CompanyScanner> {
    const res = await this.request<{ company_scanner: CompanyScanner }>(
      `/company-scanners/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.company_scanner
  }

  listIntakeSenders(
    params: { active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<IntakeSender>> {
    return this.request(`/intake-senders${query({ ...params })}`)
  }

  async createIntakeSender(body: { address: string }): Promise<IntakeSender> {
    const res = await this.request<{ intake_sender: IntakeSender }>('/intake-senders', {
      method: 'POST',
      body,
    })
    return res.intake_sender
  }

  async updateIntakeSender(
    id: string,
    body: { is_active?: boolean; row_version: number },
  ): Promise<IntakeSender> {
    const res = await this.request<{ intake_sender: IntakeSender }>(
      `/intake-senders/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.intake_sender
  }

  listVendors(params: ReferenceListParams = {}): Promise<Page<Vendor>> {
    return this.request(`/vendors${query({ ...params })}`)
  }

  async createVendor(body: Partial<Vendor> & { vendor_id: string; vendor_name: string }): Promise<Vendor> {
    const res = await this.request<{ vendor: Vendor }>('/vendors', { method: 'POST', body })
    return res.vendor
  }

  async updateVendor(vendorId: string, body: Partial<Vendor> & { row_version: number }): Promise<Vendor> {
    const res = await this.request<{ vendor: Vendor }>(`/vendors/${encodeURIComponent(vendorId)}`, {
      method: 'PATCH',
      body,
    })
    return res.vendor
  }

  listAccounts(params: ReferenceListParams = {}): Promise<Page<Account>> {
    return this.request(`/accounts${query({ ...params })}`)
  }

  async createAccount(body: { account_id: string; account_name: string }): Promise<Account> {
    const res = await this.request<{ account: Account }>('/accounts', { method: 'POST', body })
    return res.account
  }

  async updateAccount(
    accountId: string,
    body: { account_name?: string; is_active?: boolean; row_version: number },
  ): Promise<Account> {
    const res = await this.request<{ account: Account }>(`/accounts/${encodeURIComponent(accountId)}`, {
      method: 'PATCH',
      body,
    })
    return res.account
  }

  async listCostCenters(
    params: ReferenceListParams & { expense_type_code?: string } = {},
  ): Promise<CostCenter[]> {
    const res = await this.request<{ data: CostCenter[] }>(
      `/cost-centers${query({ ...params })}`,
    )
    return res.data
  }

  async listExpenseTypes(params: ReferenceListParams = {}): Promise<ExpenseType[]> {
    const res = await this.request<{ data: ExpenseType[] }>(
      `/expense-types${query({ ...params })}`,
    )
    return res.data
  }

  async listVendorCodingRules(
    params: {
      vendor_id?: string
      expense_type_code?: string
      cost_center_code?: string
      active?: boolean
    } = {},
  ): Promise<VendorCodingRule[]> {
    const res = await this.request<{ data: VendorCodingRule[] }>(
      `/vendor-coding-rules${query({ ...params })}`,
    )
    return res.data ?? []
  }

  async createVendorCodingRule(body: {
    vendor_id: string
    description_scope?: string | null
    expense_type_code?: string | null
    cost_center_code?: string | null
    source?: 'Controller' | 'Learned'
  }): Promise<VendorCodingRule> {
    const res = await this.request<{ vendor_coding_rule: VendorCodingRule }>('/vendor-coding-rules', {
      method: 'POST',
      body,
    })
    return res.vendor_coding_rule
  }

  async updateVendorCodingRule(
    id: string,
    body: {
      description_scope?: string | null
      expense_type_code?: string | null
      cost_center_code?: string | null
      source?: 'Controller' | 'Learned'
      is_active?: boolean
      row_version: number
    },
  ): Promise<VendorCodingRule> {
    const res = await this.request<{ vendor_coding_rule: VendorCodingRule }>(
      `/vendor-coding-rules/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.vendor_coding_rule
  }

  async listVendorAliases(params: { vendor_id?: string; active?: boolean } = {}): Promise<VendorAlias[]> {
    const res = await this.request<{ data: VendorAlias[] }>(`/vendor-aliases${query({ ...params })}`)
    return res.data ?? []
  }

  listVehicles(params: ReferenceListParams & { status?: string } = {}): Promise<Page<Vehicle>> {
    return this.request(`/vehicles${query({ ...params })}`)
  }

  async createVehicle(body: Partial<Vehicle> & { number: string }): Promise<Vehicle> {
    const res = await this.request<{ vehicle: Vehicle }>('/vehicles', { method: 'POST', body })
    return res.vehicle
  }

  async updateVehicle(vehicleId: string, body: Partial<Vehicle> & { row_version: number }): Promise<Vehicle> {
    const res = await this.request<{ vehicle: Vehicle }>(`/vehicles/${encodeURIComponent(vehicleId)}`, {
      method: 'PATCH',
      body,
    })
    return res.vehicle
  }

  previewVehicleImport(file: File): Promise<VehicleImportPreview> {
    const form = new FormData()
    form.append('file', file)
    return this.request('/vehicles/import/preview', { method: 'POST', form })
  }

  commitVehicleImport(previewToken: string, idempotencyKey?: string): Promise<VehicleImportResult> {
    return this.request('/vehicles/import/commit', {
      method: 'POST',
      body: { preview_token: previewToken },
      idempotencyKey: idempotencyKey ?? this.newIdempotencyKey(),
    })
  }

  // --- analytics ----------------------------------------------------------

  private analytics<T>(name: string, params: AnalyticsParams = {}): Promise<T> {
    return this.request<T>(
      `/analytics/${name}${query({
        from: params.from,
        to: params.to,
        company_id: params.company_id,
        vendor_id: params.vendor_id,
        entity_type: params.entity_type,
      })}`,
    )
  }

  async getAnalyticsOverview(params?: AnalyticsParams): Promise<AnalyticsOverview> {
    const r = await this.analytics<AnalyticsOverview>('overview', params)
    // `by_status[].total` is the contract's decimal string; every other money
    // field in this client is a number by the time a screen reads it.
    return { ...r, by_status: parseTotals(r.by_status) }
  }

  /** Aging carries no money — counts per band only, so nothing to parse. */
  getAnalyticsAging(params?: AnalyticsParams): Promise<AnalyticsAging> {
    return this.analytics('aging', params)
  }

  getAnalyticsThroughput(params?: AnalyticsParams): Promise<AnalyticsThroughput> {
    return this.analytics('throughput', params)
  }

  getAnalyticsDenials(params?: AnalyticsParams): Promise<AnalyticsDenials> {
    return this.analytics('denials', params)
  }

  async getAnalyticsSpendByEntity(params?: AnalyticsParams): Promise<AnalyticsSpendByEntity> {
    const r = await this.analytics<AnalyticsSpendByEntity>('spend-by-entity', params)
    return { ...r, entities: parseTotals(r.entities) }
  }

  getAnalyticsCorrections(params?: AnalyticsParams): Promise<AnalyticsCorrections> {
    return this.analytics('corrections', params)
  }

  // --- corrections --------------------------------------------------------

  listCorrections(params: CorrectionListParams = {}): Promise<Page<Correction>> {
    return this.request(
      `/corrections${query({
        parent_id: params.parent_id,
        parent_ids: params.parent_ids?.join(','),
        since: params.since,
        field: params.field,
        vendor_id: params.vendor_id,
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    )
  }

  // --- directory ----------------------------------------------------------

  /** Microsoft Graph, on the signed-in user's delegated token (§0.11). The
      matcher stores a staff tag as the user principal name, so that is the id. */
  async searchDirectoryUsers(term: string, top = 25): Promise<DirectoryUser[]> {
    const trimmed = term.trim()
    const filter = trimmed
      ? `startswith(displayName,'${trimmed.replace(/'/g, "''")}') or startswith(userPrincipalName,'${trimmed.replace(/'/g, "''")}')`
      : undefined
    const url = `https://graph.microsoft.com/v1.0/users${query({
      $select: 'id,displayName,userPrincipalName,mail,jobTitle,department',
      $top: top,
      $filter: filter,
    })}`
    const res = await this.request<{
      value: {
        id: string
        displayName: string
        userPrincipalName: string
        mail?: string | null
        jobTitle?: string | null
        department?: string | null
      }[]
    }>('', { absolute: url, token: graphToken })
    return (res.value ?? []).map((u) => ({
      id: u.id,
      display_name: u.displayName,
      upn: u.userPrincipalName,
      mail: u.mail ?? null,
      job_title: u.jobTitle ?? null,
      department: u.department ?? null,
    }))
  }
}

let instance: ApiClient | null = null

/** Boot order for the shell: read the runtime configuration, then complete or
    start sign-in. Anything after this may assume both. The local harness
    replaces this whole module, so its `bootstrap` is a no-op — that is how the
    mock runs with no Entra tenant and no API. */
export async function bootstrap(): Promise<void> {
  await loadConfig()
  const instance = await initAuth()
  if (!instance.getActiveAccount()) await signIn()
}

/** The single client instance the whole app shares. */
export function getClient(): ApiClient {
  if (!instance) instance = new HttpApiClient()
  return instance
}

/** Test seam: replace the implementation (the mock harness aliases this module
    wholesale, so this exists for unit tests that import the real one). */
export function setClient(client: ApiClient | null): void {
  instance = client
}

export type { ApiClient } from './contract'
